import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';

interface ThemeContextValue {
    theme: Theme;
    resolved: Resolved;
    setTheme: (t: Theme) => void;
    toggle: () => void;
}

const STORAGE_KEY = 'conexed-theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === 'light' || v === 'dark' || v === 'system') return v;
    } catch {}
    return 'system';
}

function resolve(theme: Theme): Resolved {
    if (theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
}

function apply(resolved: Resolved) {
    const root = document.documentElement;
    if (resolved === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => readStored());
    const [resolved, setResolved] = useState<Resolved>(() => resolve(readStored()));

    useEffect(() => {
        const r = resolve(theme);
        setResolved(r);
        apply(r);
        try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
    }, [theme]);

    useEffect(() => {
        if (theme !== 'system') return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            const r = resolve('system');
            setResolved(r);
            apply(r);
        };
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [theme]);

    function setTheme(t: Theme) {
        setThemeState(t);
    }

    function toggle() {
        setThemeState(resolved === 'dark' ? 'light' : 'dark');
    }

    return (
        <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
    return ctx;
}
